import { ReactNode, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

interface Props {
  onDetected: (code: string) => void;
  onClose: () => void;
  /** Не закрываться после чтения: каждый новый код уходит в onDetected (сканер на телефоне для CRM) */
  continuous?: boolean;
  title?: string;
  closeLabel?: string;
  footer?: ReactNode;
}

const FORMATS = ["ean_13", "ean_8", "upc_a", "upc_e", "code_128", "code_39", "itf", "qr_code"];
const ZXING_URL = "/zxing.min.js";

let zxingLoading: Promise<any> | null = null;
const loadZxing = () => {
  const w = window as any;
  if (w.ZXing) return Promise.resolve(w.ZXing);
  if (!zxingLoading) {
    zxingLoading = new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = ZXING_URL;
      s.onload = () => resolve(w.ZXing);
      s.onerror = () => { zxingLoading = null; reject(new Error("zxing")); };
      document.head.appendChild(s);
    });
  }
  return zxingLoading;
};

/** Сканер штрихкода камерой телефона: встроенный BarcodeDetector, а где его нет (iPhone) — библиотека ZXing */
export default function BarcodeScanner({ onDetected, onClose, continuous, title, closeLabel, footer }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [manual, setManual] = useState("");
  const [found, setFound] = useState<string | null>(null);
  const finishTimer = useRef<number | undefined>(undefined);
  const foundTimer = useRef<number | undefined>(undefined);
  const lastHit = useRef({ code: "", at: 0 });
  const contRef = useRef(!!continuous);
  const onDetectedRef = useRef(onDetected);
  onDetectedRef.current = onDetected;

  useEffect(() => {
    let stopped = false;
    let stream: MediaStream | null = null;
    let timer: number | undefined;

    const done = (code: string) => {
      if (stopped) return;
      if (contRef.current) {
        // непрерывный режим: тот же код подряд игнорируем 2,5 с, чтобы один штрихкод не ушёл десять раз
        const c = code.replace(/\s+/g, "");
        const now = Date.now();
        if (c === lastHit.current.code && now - lastHit.current.at < 2500) return;
        lastHit.current = { code: c, at: now };
        try { navigator.vibrate?.(60); } catch { /* ignore */ }
        setFound(c);
        window.clearTimeout(foundTimer.current);
        foundTimer.current = window.setTimeout(() => setFound(null), 900);
        onDetectedRef.current(c);
        return;
      }
      stopped = true;
      try { navigator.vibrate?.(60); } catch { /* ignore */ }
      const clean = code.replace(/\s+/g, "");
      // камеру гасим сразу, а код показываем крупно — чтобы было видно, что отсканировалось
      try { videoRef.current?.pause(); } catch { /* ignore */ }
      setFound(clean);
      finishTimer.current = window.setTimeout(() => onDetectedRef.current(clean), 1100);
    };

    const start = async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        setError("Камера недоступна в этом браузере. Введите код вручную.");
        return;
      }
      const video = videoRef.current;
      if (!video) return;
      try {
        try {
          stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" }, width: { ideal: 1920 }, height: { ideal: 1080 } }, audio: false });
        } catch (e: any) {
          if (e?.name === "NotAllowedError" || e?.name === "SecurityError") throw e;
          stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
        }
        if (stopped) { stream.getTracks().forEach((t) => t.stop()); return; }
        // непрерывный автофокус (iPhone поддерживает не везде — тогда просто игнорируем)
        try { await stream.getVideoTracks()[0]?.applyConstraints({ advanced: [{ focusMode: "continuous" } as any] }); } catch { /* ignore */ }
        video.setAttribute("playsinline", "true");
        video.muted = true;
        video.srcObject = stream;
        await video.play();

        const BD = (window as any).BarcodeDetector;
        let det: any = null;
        if (BD) {
          let formats = FORMATS;
          try { const sup: string[] = await BD.getSupportedFormats(); formats = FORMATS.filter((f) => sup.includes(f)); } catch { /* ignore */ }
          if (formats.length) det = new BD({ formats });
        }

        let ZX: any = null;
        let reader: any = null;
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d") as CanvasRenderingContext2D | null;
        if (!det) {
          ZX = await loadZxing();
          if (stopped) return;
          const hints = new Map();
          hints.set(ZX.DecodeHintType.POSSIBLE_FORMATS, [ZX.BarcodeFormat.EAN_13, ZX.BarcodeFormat.EAN_8, ZX.BarcodeFormat.UPC_A, ZX.BarcodeFormat.UPC_E, ZX.BarcodeFormat.CODE_128, ZX.BarcodeFormat.CODE_39, ZX.BarcodeFormat.ITF]);
          hints.set(ZX.DecodeHintType.TRY_HARDER, true);
          reader = new ZX.MultiFormatReader();
          reader.setHints(hints);
        }

        // Режем кадр: сначала центральная полоса (там штрихкод и ищет глаз), раз в три попытки — весь кадр
        const decodeZx = (n: number): string | null => {
          const vw = video.videoWidth, vh = video.videoHeight;
          if (!vw || !vh || !ctx) return null;
          const full = n % 3 === 2;
          const sw = full ? vw : Math.round(vw * 0.9);
          const sh = full ? vh : Math.round(vh * 0.5);
          const sx = Math.round((vw - sw) / 2), sy = Math.round((vh - sh) / 2);
          const scale = Math.min(1, 1000 / sw);
          canvas.width = Math.round(sw * scale);
          canvas.height = Math.round(sh * scale);
          ctx.drawImage(video, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
          try {
            const src = new ZX.HTMLCanvasElementLuminanceSource(canvas);
            const bmp = new ZX.BinaryBitmap(new ZX.HybridBinarizer(src));
            return reader.decodeWithState(bmp).getText();
          } catch {
            try {
              // штрихкод бывает светлый на тёмном
              const src = new ZX.HTMLCanvasElementLuminanceSource(canvas).invert();
              return reader.decodeWithState(new ZX.BinaryBitmap(new ZX.HybridBinarizer(src))).getText();
            } catch { return null; }
          }
        };

        let n = 0;
        const tick = async () => {
          if (stopped) return;
          try {
            if (det) {
              const res = await det.detect(video);
              if (res.length && res[0].rawValue) { done(res[0].rawValue); if (!contRef.current) return; }
            } else {
              const text = decodeZx(n++);
              if (text) { done(text); if (!contRef.current) return; }
            }
          } catch { /* кадр ещё не готов */ }
          timer = window.setTimeout(tick, det ? 150 : 90);
        };
        tick();
      } catch (e: any) {
        const name = e?.name || "";
        setError(
          name === "NotAllowedError" || name === "SecurityError"
            ? "Нет доступа к камере. Разрешите её для сайта в настройках браузера или введите код вручную."
            : "Не удалось запустить камеру. Введите код вручную.",
        );
      }
    };
    start();

    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
      if (finishTimer.current) clearTimeout(finishTimer.current);
      window.clearTimeout(foundTimer.current);
      stream?.getTracks().forEach((t) => t.stop());
      const v = videoRef.current;
      const s = v?.srcObject as MediaStream | null;
      s?.getTracks().forEach((t) => t.stop());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Через портал в body: сканер не должен зависеть от обрезки и прокрутки окна, из которого его открыли
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100dvh", zIndex: 100000, background: "#000", display: "flex", flexDirection: "column", padding: "max(10px, env(safe-area-inset-top)) 12px max(10px, env(safe-area-inset-bottom))", boxSizing: "border-box" }}
    >
      {/* Шапка с кнопкой «Закрыть» всегда сверху — её не перекроют ни клавиатура, ни панели браузера */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, flex: "none", paddingBottom: 8 }}>
        <div style={{ color: "#fff", fontWeight: 700, fontSize: 16, flex: 1 }}>{title ?? "Наведите камеру на штрихкод"}</div>
        <button type="button" onClick={onClose} aria-label="Закрыть" style={{ flex: "none", background: "#374151", color: "#fff", border: "none", borderRadius: 10, padding: "10px 16px", fontSize: 15, fontWeight: 700, cursor: "pointer" }}>
          ✕ {closeLabel ?? "Закрыть"}
        </button>
      </div>
      <div style={{ position: "relative", flex: 1, minHeight: 0, borderRadius: 14, overflow: "hidden", background: "#000" }}>
        <video ref={videoRef} playsInline muted style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        {!error && !found && (
          <div style={{ position: "absolute", left: "10%", right: "10%", top: "38%", height: "24%", border: "2px solid #818cf8", borderRadius: 12, boxShadow: "0 0 0 9999px rgba(0,0,0,0.35)", pointerEvents: "none" }} />
        )}
        {error && <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, textAlign: "center", color: "#fca5a5", fontSize: 14 }}>{error}</div>}
        {found && (
          <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10, background: "rgba(5,150,105,0.92)", color: "#fff", textAlign: "center", padding: 20 }}>
            <div style={{ fontSize: 56, lineHeight: 1 }}>✓</div>
            <div style={{ fontSize: 15, fontWeight: 600 }}>Отсканировано</div>
            <div style={{ fontSize: 26, fontWeight: 800, letterSpacing: 1, wordBreak: "break-all" }}>{found}</div>
          </div>
        )}
      </div>
      {footer && <div style={{ flex: "none", marginTop: 8, maxHeight: "22dvh", overflowY: "auto" }}>{footer}</div>}
      <div style={{ display: "flex", gap: 8, marginTop: 10, flex: "none" }}>
        <input
          className="input"
          style={{ flex: 1, minWidth: 0 }}
          inputMode="numeric"
          placeholder="Или введите код вручную"
          value={manual}
          onChange={(e) => setManual(e.target.value.replace(/\s+/g, ""))}
        />
        <button type="button" className="btn" style={{ width: "auto", flex: "none" }} disabled={!manual} onClick={() => { onDetected(manual); if (contRef.current) setManual(""); }}>OK</button>
      </div>
    </div>,
    document.body,
  );
}
